package io.jsonize.web.screens.editor

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.*
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement

// ---------------------------------------------
// Constants
// ---------------------------------------------

private const val LARGE_FILE_LIMIT_BYTES = 5 * 1024 * 1024          // 5 MB
private const val SEARCH_GUARD_LIMIT = 1_000_000                    // search disabled above 1M chars
private const val AUTO_VALIDATE_LIMIT = 5_000_000                   // skip JSON validation above 5M
private const val SYNTAX_HL_LIMIT = 5_000_000                       // raw fallback above 5M


// ---------------------------------------------
// JSON Error Model
// ---------------------------------------------


// ---------------------------------------------
// JSON Validation
// ---------------------------------------------

/**
 * Validates JSON and returns JsonError or null.
 * Non-blocking for large files.
 */
fun validateJsonSafe(text: String): JsonError? {
    if (text.isBlank()) return null
    if (text.length > AUTO_VALIDATE_LIMIT) return null

    return try {
        Json.parseToJsonElement(text)
        null
    } catch (e: Throwable) {
        parseJsonError(text, e)
    }
}

private fun parseJsonError(text: String, throwable: Throwable): JsonError {
    val raw = throwable.message ?: "Invalid JSON"

    // detect "offset 123"
    val offsetRegex = Regex("offset (\\d+)")
    val offset = offsetRegex.find(raw)?.groupValues?.getOrNull(1)?.toIntOrNull()

    // detect "line X, column Y"
    val lineColRegex = Regex("line (\\d+), column (\\d+)", RegexOption.IGNORE_CASE)
    val match = lineColRegex.find(raw)

    val line = match?.groupValues?.getOrNull(1)?.toIntOrNull()
    val col = match?.groupValues?.getOrNull(2)?.toIntOrNull()

    return when {
        line != null && col != null ->
            JsonError(raw, line, col)

        offset != null && offset in text.indices -> {
            val (lineX, colX) = computeLineCol(text, offset)
            JsonError(raw, lineX, colX)
        }

        else ->
            JsonError(raw)
    }
}


// ---------------------------------------------
// Pretty & Minify
// ---------------------------------------------

private val jsonPretty = Json { prettyPrint = true }
private val jsonCompact = Json { prettyPrint = false }

suspend fun tryPrettyPrintOrNull(text: String): String? {
    return try {
        if (text.isBlank()) return null
        val el: JsonElement = Json.parseToJsonElement(text)
        jsonPretty.encodeToString(el)
    } catch (_: Throwable) {
        null
    }
}

suspend fun tryMinifyJson(text: String): String? {
    return try {
        if (text.isBlank()) return null
        val el: JsonElement = Json.parseToJsonElement(text)
        jsonCompact.encodeToString(el)
    } catch (_: Throwable) {
        null
    }
}


// ---------------------------------------------
// Line / Column helpers
// ---------------------------------------------

fun computeLineCol(text: String, offset: Int): Pair<Int, Int> {
    var line = 1
    var col = 1
    val limit = offset.coerceIn(0, text.length)

    for (i in 0 until limit) {
        if (text[i] == '\n') {
            line++
            col = 1
        } else col++
    }
    return line to col
}

fun computeOffsetFromLineCol(text: String, line: Int, column: Int): Int {
    if (line <= 1 && column <= 1) return 0

    var currentLine = 1
    var idx = 0
    val n = text.length

    // Go to requested line
    while (idx < n && currentLine < line) {
        if (text[idx] == '\n') currentLine++
        idx++
    }

    // Now go to requested column
    var colCount = 1
    while (idx < n && colCount < column && text[idx] != '\n') {
        idx++
        colCount++
    }

    return idx.coerceIn(0, n)
}


// ---------------------------------------------
// AnnotatedString helpers (search highlight)
// ---------------------------------------------

object AnnotatedStringBuilderUtil {

    private val highlightBg = Color(0x33FFFF00) // soft yellow
    private val selectedBg = Color(0x66FFD700)  // stronger gold

    /** Returns plain text as AnnotatedString without styles. */
    fun plain(text: String): AnnotatedString =
        AnnotatedString.Builder(text).toAnnotatedString()

    /**
     * Applies background highlight to search matches on top of an existing annotated string.
     */
    fun applySearchHighlights(
        base: AnnotatedString,
        ranges: List<IntRange>,
        selectedIndex: Int
    ): AnnotatedString {
        if (ranges.isEmpty()) return base

        val builder = AnnotatedString.Builder(base)

        ranges.forEachIndexed { idx, range ->
            val color = if (idx == selectedIndex) selectedBg else highlightBg

            builder.addStyle(
                style = SpanStyle(background = color),
                start = range.first,
                end = range.last + 1
            )
        }

        return builder.toAnnotatedString()
    }
}

fun cmJumpToOffset(view: EditorView, offset: Int) {
    val docLen = view.state.doc.length as Int
    val pos = offset.coerceIn(0, docLen)

    val tr = js("({})")
    tr.selection = EditorSelection.single(pos)
    tr.scrollIntoView = true

    view.dispatch(tr)

    try { unfoldCode(view) } catch (_: dynamic) {}

    // Focus after dispatch
    window.requestAnimationFrame {
        try { view.asDynamic().focus() } catch (_: dynamic) {}
    }
}
